// PE-0000 — Conversational booking using the existing self-scheduling APIs
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PatientChatChoice, PatientChatReply } from '../models/patient-chat.model';
import { buildAppointmentViewModel } from '../shared/utils/appointment-view-model.builder';
import {
  AppointmentTypeOption,
  BookAppointmentSlot,
  LocationOption,
  OWN_PROVIDER_APPOINTMENT_TYPES_SETTING,
  ProviderOption,
  SLOT_STATUS_OPEN,
  buildBookAppointmentCalendarBounds,
  extractSlotStatusFromApiResponse,
  filterReturningPatientAppointmentTypes,
  findProviderOptionById,
  findSettingValue,
  formatBookAppointmentDisplayDate,
  groupSlotsFromApiResponse,
  isAppointmentTypeConfiguredForOwnProvider,
  isBookAppointmentDateWithinCalendarBounds,
  isSlotStatusOpen,
  mapAppointmentTypeOptions,
  mapLocationOptions,
  mapProviderOptions,
  parseAppointmentSlotsDuration,
  parseCalendarEnabledDates,
  parseAppointmentTypeSettingValue,
  parseOwnProviderAppointmentTypeIds,
  resolveAppointmentTypeColorCode,
  resolveBookLocationId,
  resolveBookProviderId,
  resolveConfirmationTopersonEmail,
  resolveLocationEmrId,
  resolveProviderIdFromOption,
  resolveTemplateIdFromSettings,
  trimAppointmentTypeIdForApi,
} from '../utils/book-appointment-settings.util';
import { ApiService } from './api.service';
import { AuthStateService } from './auth-state.service';
import { SelfSchedulingService } from './self-scheduling.service';
import { StorageService } from './storage.service';
import { TenantService } from './tenant.service';

const MAX_CHOICES = 8;

type BookingStep = 'type' | 'location' | 'provider' | 'date' | 'slot' | 'captcha';

interface BookingAlternate {
  type: AppointmentTypeOption;
  location: LocationOption;
  date: Date;
}

interface BookingSession {
  step: BookingStep;
  tenantId: string;
  patientId: string;
  templateId: string;
  helpLineNumber: string;
  minLeadDays: string;
  maxLeadDays: string;
  types: AppointmentTypeOption[];
  locations: LocationOption[];
  providers: ProviderOption[];
  ownProviderTypeIds: string[];
  ownProviderId: string;
  selectedType: AppointmentTypeOption | null;
  selectedLocation: LocationOption | null;
  selectedProvider: ProviderOption | null;
  providerLocked: boolean;
  selectedDate: Date | null;
  availableDates: Date[];
  slots: BookAppointmentSlot[];
  selectedSlot: BookAppointmentSlot | null;
  requestedDate: Date | null;
  alternates: BookingAlternate[];
  choices: PatientChatChoice[];
}

@Injectable({ providedIn: 'root' })
export class PatientChatBookingService {
  // CONCURRENCY: one signed-in patient per browser tab; session is not shared across users
  private session: BookingSession | null = null;

  private readonly schedulingService = inject(SelfSchedulingService);
  private readonly apiService = inject(ApiService);
  private readonly authStateService = inject(AuthStateService);
  private readonly storageService = inject(StorageService);
  private readonly tenantService = inject(TenantService);

  isActive(): boolean {
    return this.session !== null;
  }

  reset(): void {
    this.session = null;
  }

  matchesCurrentChoice(question: string): boolean {
    if (!this.session) {
      return false;
    }
    if (this.matchChoice(question, this.session.choices)) {
      return true;
    }
    return this.session.step === 'date' && extractBookingDate(question) !== null;
  }

  start(question = ''): Observable<PatientChatReply> {
    const tenantId = String(this.storageService.getSessionStorage('tenant_id') ?? '').trim();
    const patientId = String(this.authStateService.userInfo?.patientId ?? '').trim();
    if (!tenantId || !patientId) {
      return of(pageReply('I need you to be signed in before I can book a visit.'));
    }
    return forkJoin({
      settings: this.schedulingService.getTenantSettings(tenantId),
      locations: this.schedulingService.getLocations(tenantId),
      providers: this.schedulingService.getProviders(tenantId),
      types: this.schedulingService.getAppointmentTypes(tenantId),
      ownProvider: this.schedulingService.getPatientOwnProvider(tenantId, patientId),
    }).pipe(
      switchMap((result) => this.beginSession(tenantId, patientId, result, question)),
      catchError((error: unknown) => this.fail(error, pageReply('I could not load booking options.')))
    );
  }

  continueWithText(question: string): Observable<PatientChatReply> {
    const session = this.session;
    if (!session) {
      return this.start();
    }
    if (isCancel(question)) {
      this.reset();
      return of({ text: 'Booking is cancelled. Ask me again when you want to book a visit.' });
    }
    const choice = this.matchChoice(question, session.choices);
    if (choice) {
      return this.selectChoice(choice.id);
    }
    if (session.step === 'date') {
      const date = extractBookingDate(question);
      if (date) {
        if (!isListedDate(session.availableDates, date)) {
          return of(this.askDate('That date is not open on the calendar. Pick one of the dates below.'));
        }
        session.requestedDate = date;
        return this.applyDate(date, false);
      }
    }
    // PE-6332 — a sentence can name the visit type, location, and date together
    if (this.questionHasBookingDetails(question)) {
      return this.advanceWithQuestion(question);
    }
    if (session.step === 'captcha') {
      return of({
        text: 'Complete the check below to save this appointment, or say cancel.',
        requiresCaptcha: true,
        choices: [],
      });
    }
    // PE-6332 — an empty choice list has nothing to tap; load the options again
    if (!session.choices.length) {
      this.reset();
      return this.start();
    }
    return of({
      text: 'Choose one of the options below, or say cancel.',
      choices: session.choices,
    });
  }

  selectChoice(choiceId: string): Observable<PatientChatReply> {
    const session = this.session;
    if (!session) {
      return this.start();
    }
    if (choiceId.startsWith('type:')) {
      return this.applyType(choiceId);
    }
    if (choiceId.startsWith('alt:')) {
      return this.applyAlternate(choiceId);
    }
    if (choiceId.startsWith('location:')) {
      return this.applyLocation(choiceId);
    }
    if (choiceId === 'provider:any' || choiceId.startsWith('provider:')) {
      return this.applyProvider(choiceId);
    }
    if (choiceId.startsWith('date:')) {
      const date = session.availableDates[indexFrom(choiceId)];
      return date ? this.applyDate(date) : this.loadAvailableDates();
    }
    if (choiceId.startsWith('slot:')) {
      return of(this.applySlot(choiceId));
    }
    return of({ text: 'Choose one of the options below, or say cancel.', choices: session.choices });
  }

  confirmBooking(token: string): Observable<PatientChatReply> {
    const session = this.session;
    const slot = session?.selectedSlot;
    if (!session || session.step !== 'captcha' || !slot) {
      return of({ text: 'Pick an open time before I save the appointment.' });
    }
    if (!token.trim()) {
      return of({
        text: 'Complete the check below to save this appointment.',
        requiresCaptcha: true,
      });
    }
    return this.schedulingService.verifyRecaptchaToken(token, session.tenantId).pipe(
      switchMap((captcha) => {
        if (!isCaptchaSuccess(captcha)) {
          return of({
            text: 'The check did not pass. Complete it again to save this appointment.',
            requiresCaptcha: true,
          });
        }
        return this.schedulingService.getSlotStatus(this.slotStatusParams(session, slot)).pipe(
          switchMap((statusData) => {
            if (extractSlotStatusFromApiResponse(statusData) !== SLOT_STATUS_OPEN) {
              return throwError(() => new Error('SLOT_UNAVAILABLE'));
            }
            return this.schedulingService.bookAppointment(this.buildPayload(session, slot));
          }),
          switchMap((bookResponse) => {
            if (!isBookSuccess(bookResponse)) {
              return throwError(() => new Error('BOOK_FAILED'));
            }
            // PE-6332 — same post-book steps as the appointment page: audit, then confirmation email
            this.recordAppointmentBookedAudit(session);
            return this.sendConfirmation(session, slot).pipe(map(() => this.savedReply(slot)));
          })
        );
      }),
      catchError((error: unknown) => {
        if (error instanceof Error && error.message === 'SLOT_UNAVAILABLE') {
          session.slots = session.slots.filter((item) => item !== slot);
          session.selectedSlot = null;
          session.step = 'slot';
          return of(this.askSlot('That time is no longer open. Pick another time.'));
        }
        return this.fail(error, {
          text: 'I could not save this appointment. You can try again on the appointment page.',
          route: '/book-appointment',
          routeLabel: 'Book appointment',
        });
      })
    );
  }

  private beginSession(
    tenantId: string,
    patientId: string,
    result: {
      settings: unknown;
      locations: unknown;
      providers: unknown;
      types: unknown;
      ownProvider: { providerId?: string; providerName?: string };
    },
    question: string
  ): Observable<PatientChatReply> {
    const duration = parseAppointmentSlotsDuration(
      findSettingValue(result.settings, 'AppointmentslotsDuration')
    );
    const allTypes = mapAppointmentTypeOptions(result.types);
    const eligibility = parseAppointmentTypeSettingValue(
      findSettingValue(result.settings, 'AppointmentType')
    );
    const types = filterReturningPatientAppointmentTypes(allTypes, eligibility).slice(0, MAX_CHOICES);
    const templateId = resolveTemplateIdFromSettings(result.settings);
    if (!templateId || !types.length) {
      return of(pageReply('Booking options are not available from chat right now.'));
    }
    this.session = {
      step: 'type',
      tenantId,
      patientId,
      templateId,
      helpLineNumber: findSettingValue(result.settings, 'helpLineNumber'),
      minLeadDays: String(duration.min),
      maxLeadDays: String(duration.max),
      types,
      locations: mapLocationOptions(result.locations),
      providers: mapProviderOptions(result.providers),
      ownProviderTypeIds: parseOwnProviderAppointmentTypeIds(
        findSettingValue(result.settings, OWN_PROVIDER_APPOINTMENT_TYPES_SETTING)
      ),
      ownProviderId: String(result.ownProvider?.providerId ?? '').trim(),
      selectedType: null,
      selectedLocation: null,
      selectedProvider: null,
      providerLocked: false,
      selectedDate: null,
      availableDates: [],
      slots: [],
      selectedSlot: null,
      requestedDate: null,
      alternates: [],
      choices: [],
    };
    return this.advanceWithQuestion(question);
  }

  // PE-6332 — read visit type, location, and date from the patient's sentence
  private advanceWithQuestion(question: string): Observable<PatientChatReply> {
    this.captureHints(question);
    return this.withUnderstood(question, this.continueFromCaptured());
  }

  // PE-6332 — echo the details read from the patient's sentence before the next question
  private withUnderstood(question: string, reply$: Observable<PatientChatReply>): Observable<PatientChatReply> {
    const summary = this.understoodSummary(question);
    if (!summary) {
      return reply$;
    }
    return reply$.pipe(map((reply) => ({ ...reply, text: `I understood: ${summary}. ${reply.text}` })));
  }

  private understoodSummary(question: string): string {
    const session = this.session;
    if (!session || !question.trim()) {
      return '';
    }
    const parts: string[] = [];
    if (session.selectedType && matchCatalog(question, [session.selectedType], (item) => item.appointmenttypedescription)) {
      parts.push(session.selectedType.appointmenttypedescription);
    }
    if (session.selectedLocation && matchCatalog(question, [session.selectedLocation], (item) => item.locationdescription)) {
      parts.push(session.selectedLocation.locationdescription);
    }
    const date = extractBookingDate(question);
    if (date) {
      parts.push(formatBookAppointmentDisplayDate(date));
    }
    return parts.join(' · ');
  }

  private captureHints(question: string): void {
    const session = this.requireSession();
    const date = extractBookingDate(question);
    if (date) {
      session.requestedDate = date;
    }
    if (!session.selectedType) {
      const type = matchCatalog(question, session.types, (item) => item.appointmenttypedescription);
      if (type) {
        this.assignType(type);
      }
    }
    if (!session.selectedLocation) {
      const location = matchCatalog(question, session.locations, (item) => item.locationdescription);
      if (location) {
        session.selectedLocation = location;
      }
    }
  }

  private questionHasBookingDetails(question: string): boolean {
    const session = this.requireSession();
    return extractBookingDate(question) !== null
      || matchCatalog(question, session.types, (item) => item.appointmenttypedescription) !== null
      || matchCatalog(question, session.locations, (item) => item.locationdescription) !== null;
  }

  private assignType(type: AppointmentTypeOption): PatientChatReply | null {
    const session = this.requireSession();
    session.selectedType = type;
    session.providerLocked = isAppointmentTypeConfiguredForOwnProvider(
      String(type.appointmentType['appointmenttypeid'] ?? ''),
      session.ownProviderTypeIds
    );
    if (!session.providerLocked) {
      return null;
    }
    const own = findProviderOptionById(session.providers, session.ownProviderId);
    if (!own) {
      this.reset();
      return pageReply('This visit type must use your assigned provider, and I could not find that provider.');
    }
    session.selectedProvider = own;
    return null;
  }

  // PE-6332 — skip questions the patient already answered
  private continueFromCaptured(): Observable<PatientChatReply> {
    const session = this.requireSession();
    if (!session.selectedType) {
      return of(this.askType());
    }
    if (!session.selectedLocation) {
      return of(this.askLocation());
    }
    if (session.requestedDate) {
      return this.applyDate(session.requestedDate, true);
    }
    if (session.providerLocked && session.selectedProvider) {
      return this.loadAvailableDates(
        `This visit uses ${session.selectedProvider.providerName}. Which date do you want?`
      );
    }
    return of(this.askProvider());
  }

  private applyType(choiceId: string): Observable<PatientChatReply> {
    const session = this.requireSession();
    const type = session.types[indexFrom(choiceId)];
    if (!type) {
      return of(this.askType());
    }
    const blocked = this.assignType(type);
    if (blocked) {
      return of(blocked);
    }
    if (!session.providerLocked) {
      session.selectedProvider = null;
    }
    return this.continueFromCaptured();
  }

  private applyLocation(choiceId: string): Observable<PatientChatReply> {
    const session = this.requireSession();
    const location = session.locations[indexFrom(choiceId)];
    if (!location) {
      return of(this.askLocation());
    }
    session.selectedLocation = location;
    return this.continueFromCaptured();
  }

  private applyAlternate(choiceId: string): Observable<PatientChatReply> {
    const session = this.requireSession();
    const alternate = session.alternates[indexFrom(choiceId)];
    if (!alternate) {
      return of({
        text: 'Choose one of the openings below, or say cancel.',
        choices: session.choices,
      });
    }
    session.selectedType = alternate.type;
    session.selectedLocation = alternate.location;
    session.requestedDate = alternate.date;
    if (!session.providerLocked) {
      session.selectedProvider = null;
    }
    return this.applyDate(alternate.date, false);
  }

  private applyProvider(choiceId: string): Observable<PatientChatReply> {
    const session = this.requireSession();
    if (choiceId === 'provider:any') {
      session.selectedProvider = null;
      return this.loadAvailableDates();
    }
    const provider = session.providers[indexFrom(choiceId)];
    if (!provider) {
      return of(this.askProvider());
    }
    session.selectedProvider = provider;
    return this.loadAvailableDates();
  }

  // PE-0000 — same open dates as the Book Appointment calendar
  private loadAvailableDates(
    text = 'Which date do you want? These are the open dates on the booking calendar.'
  ): Observable<PatientChatReply> {
    const session = this.requireSession();
    const params: Record<string, string> = {
      tenantid: session.tenantId,
      providerId: session.selectedProvider ? resolveProviderIdFromOption(session.selectedProvider) : '',
      templateId: session.templateId,
      locationId: resolveLocationEmrId(session.selectedLocation),
      ampm: '',
      appointmentTypeId: trimAppointmentTypeIdForApi(
        session.selectedType?.appointmentType['appointmenttypeid']
      ),
      minValue: session.minLeadDays,
      maxValue: session.maxLeadDays,
    };
    return this.schedulingService.getCalendarDates(params).pipe(
      map((data) => {
        session.availableDates = selectableCalendarDates(parseCalendarEnabledDates(data));
        return this.askDate(text);
      }),
      catchError((error: unknown) => {
        // PE-6332 — leave the session so the next book request can show options
        this.reset();
        return this.fail(error, pageReply('I could not load the open dates. You can book on the appointment page.'));
      })
    );
  }

  private applyDate(date: Date, explainIfEmpty = false): Observable<PatientChatReply> {
    const session = this.requireSession();
    // PE-0000 — lead days are sent to the slots API, same as Book Appointment. Do not block the date here.
    if (!isDateOnBookingCalendar(date)) {
      if (explainIfEmpty) {
        return this.describeOpenConfigs();
      }
      return of(this.askDate('That date is outside the booking calendar. Pick another date.'));
    }
    session.selectedDate = date;
    session.requestedDate = date;
    const selectedDate = formatDateForApi(date);
    const params = this.slotParams(session, selectedDate);
    return this.schedulingService.getSlots(params).pipe(
      switchMap((data) => {
        const groups = groupSlotsFromApiResponse(
          data,
          selectedDate,
          params['locationId'],
          session.selectedLocation,
          params['providerId'],
          session.locations,
          session.providers
        );
        session.slots = flattenOpenSlots(groups);
        if (!session.slots.length && explainIfEmpty) {
          return this.describeOpenConfigs();
        }
        const typeName = session.selectedType?.appointmenttypedescription ?? 'that visit';
        const locationName = session.selectedLocation?.locationdescription ?? 'that location';
        return of(this.askSlot(
          `Here are open times for ${typeName} at ${locationName} on ${formatBookAppointmentDisplayDate(date)}. Pick one and I will save that appointment.`
        ));
      }),
      catchError((error: unknown) => this.fail(error, this.askDate('I could not load times for that date.')))
    );
  }

  // PE-6332 — when the chosen visit has no times, name the visits that do
  private describeOpenConfigs(): Observable<PatientChatReply> {
    const session = this.requireSession();
    const typeName = session.selectedType?.appointmenttypedescription ?? 'that visit';
    const locationName = session.selectedLocation?.locationdescription ?? 'that location';
    const when = session.requestedDate
      ? formatBookAppointmentDisplayDate(session.requestedDate)
      : 'that date';
    return this.calendarDatesFor(session.selectedType, session.selectedLocation).pipe(
      switchMap((dates) => {
        const others = dates
          .filter((item) => !session.requestedDate || !sameDay(item, session.requestedDate))
          .slice(0, MAX_CHOICES);
        if (others.length) {
          session.availableDates = others;
          session.requestedDate = null;
          return of(this.askDate(
            `No open times on ${when} for ${typeName} at ${locationName}. These dates have openings for that visit.`
          ));
        }
        return this.searchOtherLocations(when, typeName, locationName);
      })
    );
  }

  private searchOtherLocations(
    when: string,
    typeName: string,
    locationName: string
  ): Observable<PatientChatReply> {
    const session = this.requireSession();
    const others = session.locations
      .filter((location) => location !== session.selectedLocation)
      .slice(0, MAX_CHOICES);
    if (!others.length || !session.selectedType) {
      return this.searchOtherTypes(when, typeName, locationName);
    }
    return forkJoin(
      others.map((location) =>
        this.calendarDatesFor(session.selectedType, location).pipe(
          map((dates) => ({ location, dates })),
          catchError(() => of({ location, dates: [] as Date[] }))
        )
      )
    ).pipe(
      switchMap((results) => {
        const hits = results.filter((item) => item.dates.length && session.selectedType);
        if (!hits.length || !session.selectedType) {
          return this.searchOtherTypes(when, typeName, locationName);
        }
        return of(this.presentAlternates(
          hits.slice(0, MAX_CHOICES).map((hit) => ({
            type: session.selectedType as AppointmentTypeOption,
            location: hit.location,
            date: hit.dates[0],
          })),
          `No open times on ${when} for ${typeName} at ${locationName}. Openings are available for:`
        ));
      })
    );
  }

  private searchOtherTypes(
    when: string,
    typeName: string,
    locationName: string
  ): Observable<PatientChatReply> {
    const session = this.requireSession();
    const location = session.selectedLocation;
    const others = session.types
      .filter((type) => type !== session.selectedType)
      .slice(0, MAX_CHOICES);
    if (!location || !others.length) {
      this.reset();
      return of(pageReply(
        `No open times on ${when} for ${typeName} at ${locationName}. No other visit on the calendar has an opening.`
      ));
    }
    return forkJoin(
      others.map((type) =>
        this.calendarDatesFor(type, location).pipe(
          map((dates) => ({ type, dates })),
          catchError(() => of({ type, dates: [] as Date[] }))
        )
      )
    ).pipe(
      map((results) => {
        const hits = results.filter((item) => item.dates.length);
        if (!hits.length) {
          this.reset();
          return pageReply(
            `No open times on ${when} for ${typeName} at ${locationName}. No other visit on the calendar has an opening.`
          );
        }
        return this.presentAlternates(
          hits.slice(0, MAX_CHOICES).map((hit) => ({
            type: hit.type,
            location,
            date: hit.dates[0],
          })),
          `No open times on ${when} for ${typeName} at ${locationName}. Openings are available for:`
        );
      })
    );
  }

  private presentAlternates(alternates: BookingAlternate[], text: string): PatientChatReply {
    const session = this.requireSession();
    session.alternates = alternates;
    session.step = 'slot';
    session.choices = alternates.map((item, index) => ({
      id: `alt:${index}`,
      label: `${item.type.appointmenttypedescription} · ${item.location.locationdescription} · ${formatBookAppointmentDisplayDate(item.date)}`,
    }));
    return { text, choices: session.choices };
  }

  private calendarDatesFor(
    type: AppointmentTypeOption | null,
    location: LocationOption | null
  ): Observable<Date[]> {
    const session = this.requireSession();
    const params: Record<string, string> = {
      tenantid: session.tenantId,
      providerId: session.selectedProvider ? resolveProviderIdFromOption(session.selectedProvider) : '',
      templateId: session.templateId,
      locationId: resolveLocationEmrId(location),
      ampm: '',
      appointmentTypeId: trimAppointmentTypeIdForApi(type?.appointmentType['appointmenttypeid']),
      minValue: session.minLeadDays,
      maxValue: session.maxLeadDays,
    };
    return this.schedulingService.getCalendarDates(params).pipe(
      map((data) => selectableCalendarDates(parseCalendarEnabledDates(data)))
    );
  }

  private slotParams(session: BookingSession, selectedDate: string): Record<string, string> {
    return {
      tenantid: session.tenantId,
      providerId: session.selectedProvider ? resolveProviderIdFromOption(session.selectedProvider) : '',
      templateId: session.templateId,
      locationId: resolveLocationEmrId(session.selectedLocation),
      selectedDate,
      ampm: '',
      appointmentTypeId: trimAppointmentTypeIdForApi(
        session.selectedType?.appointmentType['appointmenttypeid']
      ),
      minValue: session.minLeadDays,
      maxValue: session.maxLeadDays,
    };
  }

  private applySlot(choiceId: string): PatientChatReply {
    const session = this.requireSession();
    const slot = session.slots[indexFrom(choiceId)];
    if (!slot) {
      return this.askSlot('Pick one of the open times.');
    }
    session.selectedSlot = slot;
    session.step = 'captcha';
    session.choices = [];
    return {
      text: `You selected ${slot.displayLabel || slot.formattedTime || 'that time'}. Complete the check below and I will save the appointment.`,
      requiresCaptcha: true,
    };
  }

  private askType(): PatientChatReply {
    const session = this.requireSession();
    session.step = 'type';
    session.choices = session.types.map((type, index) => ({
      id: `type:${index}`,
      label: type.appointmenttypedescription,
    }));
    return {
      text: 'Which appointment type do you want?',
      choices: session.choices,
    };
  }

  private askLocation(): PatientChatReply {
    const session = this.requireSession();
    session.step = 'location';
    session.choices = session.locations.slice(0, MAX_CHOICES).map((location, index) => ({
      id: `location:${index}`,
      label: location.locationdescription,
    }));
    if (!session.choices.length) {
      this.reset();
      return pageReply('No locations are available for booking.');
    }
    return { text: 'Which location do you want?', choices: session.choices };
  }

  private askProvider(): PatientChatReply {
    const session = this.requireSession();
    session.step = 'provider';
    session.choices = [
      { id: 'provider:any', label: 'Any provider' },
      ...session.providers.slice(0, MAX_CHOICES - 1).map((provider, index) => ({
        id: `provider:${index}`,
        label: provider.providerName,
      })),
    ];
    return { text: 'Which provider do you want?', choices: session.choices };
  }

  private askDate(text = 'Which date do you want? These are the open dates on the booking calendar.'): PatientChatReply {
    const session = this.requireSession();
    session.step = 'date';
    session.choices = session.availableDates.map((date, index) => ({
      id: `date:${index}`,
      label: formatBookAppointmentDisplayDate(date),
    }));
    if (!session.choices.length) {
      // PE-6332 — do not keep a session that has no buttons to choose
      this.reset();
      return pageReply('No dates are open for that visit. You can try the appointment page.');
    }
    return { text, choices: session.choices };
  }

  private askSlot(text: string): PatientChatReply {
    const session = this.requireSession();
    session.step = 'slot';
    session.choices = session.slots.slice(0, MAX_CHOICES).map((slot, index) => ({
      id: `slot:${index}`,
      label: slotLabel(slot),
    }));
    if (!session.choices.length) {
      return this.askDate('There are no open times that day. Pick another date.');
    }
    return { text, choices: session.choices };
  }

  private buildPayload(session: BookingSession, slot: BookAppointmentSlot) {
    const userInfo = this.authStateService.userInfo;
    const providerId = resolveBookProviderId(
      session.selectedProvider ? resolveProviderIdFromOption(session.selectedProvider) : '',
      slot
    );
    const locationId = resolveBookLocationId(resolveLocationEmrId(session.selectedLocation), slot);
    return buildAppointmentViewModel({
      tenantId: session.tenantId,
      patientId: session.patientId,
      appointmentTypeId: trimAppointmentTypeIdForApi(
        session.selectedType?.appointmentType['appointmenttypeid']
      ),
      appointmentTypeDescription: session.selectedType?.appointmenttypedescription ?? '',
      providerId,
      providerName: session.selectedProvider?.providerName ?? slot.providerName ?? '',
      locationId,
      locationName: session.selectedLocation?.locationdescription ?? slot.locationName ?? '',
      slot,
      colorcode: resolveAppointmentTypeColorCode(session.selectedType?.appointmentType),
      patient: {
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
        dateOfBirth: userInfo?.patientDOB,
        gender: userInfo?.patientGender,
      },
      selectedCalendarDate: session.selectedDate,
    });
  }

  private slotStatusParams(session: BookingSession, slot: BookAppointmentSlot): Record<string, string> {
    return {
      tenantid: session.tenantId,
      providerId: resolveBookProviderId(
        session.selectedProvider ? resolveProviderIdFromOption(session.selectedProvider) : '',
        slot
      ),
      templateId: session.templateId,
      selectedDate: slot.dtStartDate?.trim() || (session.selectedDate ? formatDateForApi(session.selectedDate) : ''),
      dtStartTime: String(slot.dtStartTime ?? ''),
      dtEndTime: String(slot.dtEndTime ?? ''),
    };
  }

  private sendConfirmation(session: BookingSession, slot: BookAppointmentSlot): Observable<unknown> {
    const userInfo = this.authStateService.userInfo;
    const email = resolveConfirmationTopersonEmail(this.storageService, userInfo);
    const tenantName =
      String(this.storageService.getSessionStorage('tenant_name') ?? '').trim() ||
      this.tenantService.getCurrentTenant()?.tenantName ||
      userInfo?.tenantName ||
      '';
    return this.schedulingService
      .sendAppointmentConfirmationMail(session.tenantId, email, tenantName, {
        patientName: userInfo?.patientName ?? '',
        appointmentType: session.selectedType?.appointmenttypedescription ?? '',
        providerName: session.selectedProvider?.providerName ?? slot.providerName ?? '',
        appointmentDate: slot.formattedDate ?? (session.selectedDate ? formatDateForApi(session.selectedDate) : ''),
        appointmentTime: slot.formattedTime ?? slot.displayLabel ?? '',
        appointmentLocation: session.selectedLocation?.locationdescription ?? slot.locationName ?? '',
        contactPhone: this.schedulingService.formatPhoneNumber(session.helpLineNumber),
        departmentName: '',
      })
      .pipe(catchError(() => of(null)));
  }

  // PE-6332 — same insertauditlog row as book-appointment; a failed audit must not undo the saved visit
  private recordAppointmentBookedAudit(session: BookingSession): void {
    const tenantid = session.tenantId.trim();
    const nPatientID = this.normalizePatientIdForAudit(session.patientId);
    if (!tenantid || !nPatientID) {
      return;
    }
    this.apiService
      .insertPatientPortalAuditLog({
        tenantid,
        nPatientID,
        sActivityModule: 'Appointment',
        sActivityCategory: 'AppointmentBooking',
        sActivityType: 'appointmentBooked',
        sDescription: 'Appointment Added',
      })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private normalizePatientIdForAudit(patientId: string): string {
    const trimmed = (patientId ?? '').trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
      return '';
    }
    return trimmed.split('-')[0].trim();
  }

  private savedReply(slot: BookAppointmentSlot): PatientChatReply {
    this.reset();
    const when = slot.displayLabel || slot.formattedTime || 'the selected time';
    return {
      text: `Your appointment is saved for ${when}.`,
      route: '/appointments',
      routeLabel: 'Open appointments',
    };
  }

  private requireSession(): BookingSession {
    if (!this.session) {
      throw new Error('BOOKING_SESSION_MISSING');
    }
    return this.session;
  }

  private matchChoice(question: string, choices: PatientChatChoice[]): PatientChatChoice | null {
    const normalized = question.trim().toLowerCase();
    if (!normalized) {
      return null;
    }
    const byNumber = choices.find((_, index) => normalized === String(index + 1));
    if (byNumber) {
      return byNumber;
    }
    return (
      choices.find((choice) => choice.label.toLowerCase() === normalized) ??
      choices.find((choice) => normalized.length > 2 && choice.label.toLowerCase().includes(normalized)) ??
      null
    );
  }

  private fail(error: unknown, reply: PatientChatReply): Observable<PatientChatReply> {
    const status = (error as { status?: number })?.status;
    console.error('PE-0000 patient chat booking failed', this.session?.step ?? 'none', status ?? 'unknown');
    return of(reply);
  }
}

function pageReply(text: string): PatientChatReply {
  return {
    text,
    route: '/book-appointment',
    routeLabel: 'Book appointment',
  };
}

function indexFrom(choiceId: string): number {
  const raw = choiceId.split(':')[1] ?? '';
  const index = Number(raw);
  return Number.isInteger(index) ? index : -1;
}

function isCancel(question: string): boolean {
  const value = question.trim().toLowerCase();
  return value === 'cancel' || value === 'stop' || value === 'never mind' || value === 'nevermind';
}

function formatDateForApi(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

const MONTH_NUMBERS: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11,
  dec: 12, december: 12,
};

const MONTH_PATTERN = Object.keys(MONTH_NUMBERS).sort((a, b) => b.length - a.length).join('|');

const BOOKING_STOP_WORDS = new Set([
  'book', 'appt', 'appointment', 'visit', 'for', 'the', 'and', 'with', 'from',
  'want', 'please', 'schedule', 'today', 'tomorrow', 'that', 'this', 'date',
  ...Object.keys(MONTH_NUMBERS),
]);

// PE-6332 — "20 November 2026", "Nov 20", "november 20th, 2026"; a past day without a year moves to next year
function extractWordDate(value: string, today: Date): Date | null {
  const dayFirst = value.match(new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?(${MONTH_PATTERN})\\.?(?:,?\\s+(\\d{4}))?\\b`));
  const monthFirst = value.match(new RegExp(`\\b(${MONTH_PATTERN})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?\\b`));
  let day: number;
  let month: number;
  let year: number | null;
  if (dayFirst) {
    day = Number(dayFirst[1]);
    month = MONTH_NUMBERS[dayFirst[2]];
    year = dayFirst[3] ? Number(dayFirst[3]) : null;
  } else if (monthFirst) {
    month = MONTH_NUMBERS[monthFirst[1]];
    day = Number(monthFirst[2]);
    year = monthFirst[3] ? Number(monthFirst[3]) : null;
  } else {
    return null;
  }
  if (year !== null) {
    return validDate(year, month, day);
  }
  const thisYear = validDate(today.getFullYear(), month, day);
  if (thisYear && thisYear.getTime() >= today.getTime()) {
    return thisYear;
  }
  return validDate(today.getFullYear() + 1, month, day);
}

// PE-6332 — day-month-year inside a sentence, for example 20-11-2026
function extractBookingDate(text: string): Date | null {
  const value = text.trim().toLowerCase();
  const today = startOfDay(new Date());
  if (/\btoday\b/.test(value)) {
    return today;
  }
  if (/\btomorrow\b/.test(value)) {
    const next = new Date(today);
    next.setDate(next.getDate() + 1);
    return next;
  }
  const dayMonth = value.match(/\b(\d{1,2})[-.](\d{1,2})[-.](\d{4})\b/);
  if (dayMonth) {
    const parsed = validDate(Number(dayMonth[3]), Number(dayMonth[2]), Number(dayMonth[1]));
    if (parsed) {
      return parsed;
    }
  }
  const iso = value.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) {
    const parsed = validDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
    if (parsed) {
      return parsed;
    }
  }
  const slash = value.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
  if (slash) {
    const year = Number(slash[3]);
    const first = Number(slash[1]);
    const second = Number(slash[2]);
    return validDate(year, first, second) ?? validDate(year, second, first);
  }
  return extractWordDate(value, today);
}

function matchCatalog<T>(question: string, items: T[], labelOf: (item: T) => string): T | null {
  const compactQuestion = compactLabel(question);
  let best: { item: T; score: number } | null = null;
  for (const item of items) {
    const key = compactLabel(labelOf(item));
    if (key.length < 3) {
      continue;
    }
    let score = 0;
    if (compactQuestion.includes(key)) {
      score = key.length + 100;
    } else {
      const tokens = question.toLowerCase().split(/[^a-z0-9]+/);
      for (const token of tokens) {
        if (token.length < 4 || BOOKING_STOP_WORDS.has(token)) {
          continue;
        }
        const compactToken = compactLabel(token);
        if (key === compactToken || key.startsWith(compactToken)) {
          score = Math.max(score, compactToken.length);
        }
      }
    }
    if (score > 0 && (!best || score > best.score)) {
      best = { item, score };
    }
  }
  return best?.item ?? null;
}

function compactLabel(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function sameDay(left: Date, right: Date): boolean {
  return startOfDay(left).getTime() === startOfDay(right).getTime();
}

function validDate(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return startOfDay(date);
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function selectableCalendarDates(dates: Date[]): Date[] {
  return dates.filter((date) => isDateOnBookingCalendar(date));
}

function isListedDate(dates: Date[], selected: Date): boolean {
  const day = startOfDay(selected).getTime();
  return dates.some((date) => startOfDay(date).getTime() === day);
}

function isDateOnBookingCalendar(date: Date): boolean {
  const selected = startOfDay(date).getTime();
  if (selected < startOfDay(new Date()).getTime()) {
    return false;
  }
  const bounds = buildBookAppointmentCalendarBounds();
  return isBookAppointmentDateWithinCalendarBounds(date, bounds.minStartDate, bounds.maxStartDate);
}

function flattenOpenSlots(groups: { providers: { availability: { slots: BookAppointmentSlot[] }[] }[] }[]): BookAppointmentSlot[] {
  const slots: BookAppointmentSlot[] = [];
  for (const location of groups) {
    for (const provider of location.providers) {
      for (const day of provider.availability) {
        for (const slot of day.slots) {
          if (isSlotStatusOpen(slot.slotStatus)) {
            slots.push(slot);
          }
        }
      }
    }
  }
  return slots;
}

function slotLabel(slot: BookAppointmentSlot): string {
  const time = slot.displayLabel || slot.formattedTime || 'Open time';
  const provider = slot.providerName ? ` · ${slot.providerName}` : '';
  return `${time}${provider}`;
}

function isCaptchaSuccess(response: unknown): boolean {
  const body = response as { success?: boolean | { valueType?: string } };
  if (body?.success === true) {
    return true;
  }
  const nested = body?.success;
  return typeof nested === 'object' && nested !== null && nested.valueType === 'TRUE';
}

function isBookSuccess(response: unknown): boolean {
  if (response == null || typeof response !== 'object') {
    return false;
  }
  const success = (response as { success?: unknown }).success;
  return success === 'true' || success === true;
}
